import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import type { SystemInfo, UserDetail } from '../api/types';
import { Avatar } from '../components/Avatar';
import { RolePill } from '../components/RolePill';
import { can } from '../lib/roles';
import { roleVariant } from '../lib/systemRoles';
import { getRuntimeConfig } from '../runtimeConfig';
import { useApp } from '../state/AppContext';
import { useToast } from '../state/ToastContext';
import {
  allRolesChosen,
  chooseRole,
  failedIds,
  isHeld,
  pluralize,
  progressLabel,
  selectedIds,
  successToast,
  toggleSystem,
  type GrantOutcomes,
  type Selection,
} from '../utils/grants';
import { photoSrc } from '../utils/photo';

const STEPS = ['Systems', 'Roles', 'Duration', 'Review'];
const ALL_CATEGORIES = 'All';

const DURATIONS: Array<{ label: string; days: number | null; note: string }> = [
  { label: '30 days', days: 30, note: 'Revoked automatically after a month' },
  { label: '90 days', days: 90, note: 'Revoked automatically after a quarter' },
  { label: '1 year', days: 365, note: 'Revoked automatically after a year' },
  { label: 'Permanent', days: null, note: 'Stays until somebody revokes it' },
];

function expiryDate(days: number | null): string {
  if (days === null) return '—';
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function CheckIcon({ size = 13, stroke = 3 }: { size?: number; stroke?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M20 6L9 17l-5-5"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path
        d="M10 3L5 8l5 5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function GrantAccessPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { me, systems, refresh } = useApp();
  const { showToast } = useToast();
  const { apiUrl } = getRuntimeConfig();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [step, setStep] = useState(0);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState(ALL_CATEGORIES);
  const [selection, setSelection] = useState<Selection>({});
  const [days, setDays] = useState<number | null>(null);
  const [outcomes, setOutcomes] = useState<GrantOutcomes>({});
  const [progress, setProgress] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allowed = can(me.clientRole, 'manageGrants');

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setUser(await api.user(id));
      setLoadError(null);
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 404) {
        setLoadError('User not found.');
      } else {
        setLoadError(cause instanceof Error ? cause.message : String(cause));
      }
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const held = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const grant of user?.grants ?? []) {
      map.set(grant.systemId, [...(map.get(grant.systemId) ?? []), grant.role]);
    }
    return map;
  }, [user]);

  const lockReason = (system: SystemInfo): string | null => {
    const roles = held.get(system.id);
    if (!roles) return null;
    if (system.kind === 'Outline') return 'Already granted — change the role in Outline itself';
    const holdsAll = system.roles.every((option) => isHeld(roles, option.name));
    return holdsAll ? 'Already holds every role on this system' : null;
  };

  const categories = useMemo(() => {
    const names: string[] = [];
    for (const system of systems) {
      if (!names.includes(system.category)) {
        names.push(system.category);
      }
    }
    return [ALL_CATEGORIES, ...names];
  }, [systems]);

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matches = (system: SystemInfo) =>
      (category === ALL_CATEGORIES || system.category === category) &&
      (!query ||
        system.name.toLowerCase().includes(query) ||
        system.category.toLowerCase().includes(query) ||
        system.roles.some((option) => option.name.toLowerCase().includes(query)));
    return systems.filter(matches);
  }, [systems, search, category]);

  const chosen = useMemo(
    () => systems.filter((system) => system.id in selection),
    [systems, selection],
  );
  const entries = chosen.map((system) => ({
    system,
    role: selection[system.id],
    current: held.get(system.id) ?? [],
  }));
  const count = entries.length;
  const rolesPicked = entries.filter((entry) => entry.role !== null).length;
  const replacing = entries.filter((entry) => entry.current.length > 0);
  const duration = DURATIONS.find((option) => option.days === days) ?? DURATIONS[3];
  const grantedCount = systems.filter((system) => held.has(system.id)).length;
  const filtering = search.trim().length > 0 || category !== ALL_CATEGORIES;
  const started = Object.keys(outcomes).length > 0;
  const failed = failedIds(outcomes);

  const canContinue =
    (step === 0 && count > 0) ||
    (step === 1 && allRolesChosen(selection)) ||
    step === 2;

  const hint =
    step === 0
      ? count > 0
        ? `${pluralize(count, 'system')} selected`
        : 'Select at least one system'
      : step === 1
        ? `${rolesPicked} of ${pluralize(count, 'role')} picked`
        : step === 2
          ? duration.days === null
            ? 'No expiry date'
            : `Expires ${expiryDate(duration.days)}`
          : (progress ?? 'Takes effect immediately');

  async function submit(targets: string[]) {
    if (!user || targets.length === 0) return;
    setSubmitting(true);
    setError(null);
    const next: GrantOutcomes = { ...outcomes };
    for (let index = 0; index < targets.length; index += 1) {
      const systemId = targets[index];
      const role = selection[systemId];
      if (!role) continue;
      setProgress(progressLabel(index + 1, targets.length));
      try {
        await api.grant(user.id, { systemId, role, expiresInDays: days });
        next[systemId] = { ok: true };
      } catch (cause) {
        next[systemId] = {
          ok: false,
          error: cause instanceof Error ? cause.message : String(cause),
        };
      }
      setOutcomes({ ...next });
    }
    setProgress(null);
    setSubmitting(false);
    void refresh();
    const remaining = failedIds(next);
    if (remaining.length === 0) {
      showToast(
        successToast(
          entries.map((entry) => ({
            systemName: entry.system.name,
            role: entry.role ?? '',
            replaced: entry.current,
          })),
        ),
      );
      navigate(`/users/${user.id}`);
      return;
    }
    setError(
      `${pluralize(remaining.length, 'grant')} failed — fix the cause and retry, or finish with Done.`,
    );
  }

  if (!allowed) {
    return (
      <div className="page">
        <div className="error-banner">
          Access denied — granting access requires the admin role.
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="page">
        <Link className="back-link" to="/users">
          ← All users
        </Link>
        <div className="error-banner">{loadError}</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="page">
        <div className="empty-state">Loading user…</div>
      </div>
    );
  }

  const back = () => navigate(`/users/${user.id}`);

  return (
    <div className="page page-narrow">
      <Link className="back-link" to={`/users/${user.id}`}>
        <ChevronLeftIcon />
        {user.name}
      </Link>

      <div className="wizard-header">
        <div>
          <h1>Grant access</h1>
          <div className="page-sub">
            Give <strong>{user.name}</strong> access to one or more connected systems.
          </div>
        </div>
        <div className="wizard-badge">
          <span className="wizard-badge-dot" />
          Every grant is written to the audit log
        </div>
      </div>

      <ol className="wizard-steps">
        {STEPS.map((label, index) => {
          const state = index === step ? 'current' : index < step ? 'done' : 'todo';
          return (
            <li key={label} className="wizard-step-item">
              <button
                type="button"
                className={`wizard-step ${state}`}
                disabled={index >= step || started}
                onClick={() => setStep(index)}
              >
                <span className="wizard-step-index">
                  {state === 'done' ? <CheckIcon /> : index + 1}
                </span>
                <span className="wizard-step-label">{label}</span>
              </button>
              {index < STEPS.length - 1 && <span className="wizard-step-dash" />}
            </li>
          );
        })}
      </ol>

      <div className="card wizard-card">
        <div className="wizard-head">
          <div>
            <div className="wizard-title">
              {step === 0 && 'Which systems?'}
              {step === 1 &&
                (count === 1
                  ? `Which role on ${entries[0]?.system.name ?? 'this system'}?`
                  : 'Which role on each system?')}
              {step === 2 && 'For how long?'}
              {step === 3 && 'Review'}
            </div>
            <div className="wizard-sub">
              {step === 0 &&
                (filtering
                  ? `${visible.length} ${visible.length === 1 ? 'system matches' : 'systems match'}`
                  : `${systems.length} connected systems · ${grantedCount} already granted`)}
              {step === 1 &&
                'One role per system — picking a different one replaces the current role.'}
              {step === 2 &&
                (count > 1
                  ? 'One duration for every selected system — time-limited grants are revoked automatically when they expire.'
                  : 'Time-limited grants are revoked automatically when they expire.')}
              {step === 3 &&
                (count === 1
                  ? 'Check the details below, then grant.'
                  : `Check the ${count} grants below, then apply them in one go.`)}
            </div>
          </div>
          {step === 0 && (
            <div className="search-wrap search-wrap-sm">
              <svg
                width="15"
                height="15"
                viewBox="0 0 16 16"
                fill="none"
                className="search-icon"
              >
                <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
                <path
                  d="M10.5 10.5L14 14"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
              <input
                className="search-input"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search system or role…"
              />
            </div>
          )}
        </div>

        {step === 0 && (
          <>
            <div className="wizard-filters">
              {categories.map((name) => (
                <button
                  key={name}
                  type="button"
                  className={`wizard-filter${category === name ? ' active' : ''}`}
                  onClick={() => setCategory(name)}
                >
                  {name}
                </button>
              ))}
            </div>
            <div className="wizard-body">
              {count > 0 && (
                <div className="wizard-selection-bar">
                  <span>
                    {pluralize(count, 'system')} selected
                    {count > 1 ? ' — one role each, one shared duration' : ''}
                  </span>
                  <button type="button" className="link-button" onClick={() => setSelection({})}>
                    Clear selection
                  </button>
                </div>
              )}
              <div className="wizard-sys-grid">
                {visible.map((system) => {
                  const heldRoles = held.get(system.id);
                  const locked = lockReason(system);
                  const picked = system.id in selection;
                  const inner = (
                    <>
                      <span
                        className="sys-icon sys-icon-lg"
                        style={{ background: system.color }}
                      >
                        {system.abbr}
                      </span>
                      <span className="wizard-sys-meta">
                        <span className="wizard-sys-cat">{system.category}</span>
                        <span className="wizard-sys-name">{system.name}</span>
                      </span>
                      {locked ? (
                        <span className="wizard-sys-lock">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                            <rect
                              x="4"
                              y="11"
                              width="16"
                              height="10"
                              rx="2"
                              stroke="currentColor"
                              strokeWidth="2.2"
                            />
                            <path
                              d="M8 11V8a4 4 0 0 1 8 0v3"
                              stroke="currentColor"
                              strokeWidth="2.2"
                              strokeLinecap="round"
                            />
                          </svg>
                          Granted
                        </span>
                      ) : heldRoles ? (
                        <span className="wizard-sys-held" title={heldRoles.join(', ')}>
                          Granted · {heldRoles.join(', ')}
                        </span>
                      ) : (
                        <span className="wizard-sys-roles">
                          {system.roles.length} {system.roles.length === 1 ? 'role' : 'roles'}
                        </span>
                      )}
                      {picked && (
                        <span className="wizard-check">
                          <CheckIcon size={12} stroke={3.2} />
                        </span>
                      )}
                    </>
                  );
                  return locked ? (
                    <div key={system.id} className="wizard-sys disabled" title={locked}>
                      {inner}
                    </div>
                  ) : (
                    <button
                      key={system.id}
                      type="button"
                      className={`wizard-sys${picked ? ' picked' : ''}`}
                      aria-pressed={picked}
                      onClick={() =>
                        setSelection(
                          toggleSystem(
                            selection,
                            system.id,
                            system.roles.map((option) => option.name),
                            heldRoles ?? [],
                          ),
                        )
                      }
                    >
                      {inner}
                    </button>
                  );
                })}
              </div>
              {visible.length === 0 && (
                <div className="empty-state">No system matches that search.</div>
              )}
            </div>
          </>
        )}

        {step === 1 && (
          <div className="wizard-body">
            {entries.map((entry) => (
              <div key={entry.system.id} className="role-group">
                {count > 1 && (
                  <div className="role-group-head">
                    <span
                      className="sys-icon sys-icon-lg"
                      style={{ background: entry.system.color }}
                    >
                      {entry.system.abbr}
                    </span>
                    <span>
                      <span className="role-group-name">{entry.system.name}</span>
                      <span className="wizard-sys-cat">{entry.system.category}</span>
                    </span>
                  </div>
                )}
                {entry.current.length > 0 && (
                  <div className="wizard-replace-note">
                    Currently: <strong>{entry.current.join(', ')}</strong> — will be replaced
                  </div>
                )}
                <div className="role-list">
                  {entry.system.roles.map((option) => {
                    const picked = entry.role === option.name;
                    const owned = isHeld(entry.current, option.name);
                    return (
                      <button
                        key={option.name}
                        type="button"
                        className={`role-option${picked ? ' picked' : ''}`}
                        disabled={owned}
                        onClick={() =>
                          setSelection(chooseRole(selection, entry.system.id, option.name))
                        }
                      >
                        <span className={`radio${picked ? ' picked' : ''}`}>
                          <span className="radio-fill" />
                        </span>
                        <span className="role-option-body">
                          <span className="role-option-head">
                            <RolePill role={option.name} />
                            {roleVariant(option.name) === 'admin' && (
                              <span className="role-flag">Highest privilege</span>
                            )}
                            {owned && (
                              <span className="role-flag role-flag-current">Current</span>
                            )}
                          </span>
                          <span className="role-option-desc">{option.description}</span>
                        </span>
                        {picked && (
                          <span className="wizard-check">
                            <CheckIcon size={12} stroke={3.2} />
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {entry.system.roles.length === 0 && (
                  <div className="empty-state">This system defines no grantable roles.</div>
                )}
              </div>
            ))}
          </div>
        )}

        {step === 2 && (
          <div className="wizard-body">
            <div className="duration-grid">
              {DURATIONS.map((option) => {
                const picked = days === option.days;
                return (
                  <button
                    key={option.label}
                    type="button"
                    className={`duration-option${picked ? ' picked' : ''}`}
                    onClick={() => setDays(option.days)}
                  >
                    <span className="duration-option-label">{option.label}</span>
                    <span className="duration-option-note">{option.note}</span>
                    <span className="mono muted">
                      {option.days === null ? 'no expiry' : `until ${expiryDate(option.days)}`}
                    </span>
                    {picked && (
                      <span className="wizard-check">
                        <CheckIcon size={12} stroke={3.2} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="wizard-body">
            <div className="review-list">
              <div className="review-row">
                <div className="field-label">User</div>
                <div className="review-user">
                  <Avatar
                    name={user.name}
                    photoSrc={photoSrc(apiUrl, user.id, user.photoVersion)}
                  />
                  <span>
                    <span className="review-user-name">{user.name}</span>
                    <span className="review-user-sub mono">
                      {user.email ?? user.username}
                    </span>
                  </span>
                </div>
              </div>
              <div className="review-row">
                <div className="field-label">{count === 1 ? 'System' : 'Systems'}</div>
                <div className="review-grants">
                  {entries.map((entry) => {
                    const outcome = outcomes[entry.system.id];
                    return (
                      <div key={entry.system.id} className="review-grant">
                        <div className="review-grant-sys">
                          <span
                            className="sys-icon"
                            style={{ background: entry.system.color }}
                          >
                            {entry.system.abbr}
                          </span>
                          <span>
                            <span className="review-sys-name">{entry.system.name}</span>
                            <span className="wizard-sys-cat">{entry.system.category}</span>
                          </span>
                        </div>
                        <div className="review-grant-role">
                          {entry.role && <RolePill role={entry.role} />}
                          {entry.current.length > 0 && (
                            <span className="review-grant-replaces">
                              replaces {entry.current.join(', ')}
                            </span>
                          )}
                        </div>
                        <div>
                          {outcome && (
                            <span
                              className={`review-grant-status ${outcome.ok ? 'ok' : 'failed'}`}
                            >
                              {outcome.ok ? 'Granted' : 'Failed'}
                            </span>
                          )}
                        </div>
                        {outcome && !outcome.ok && (
                          <div className="review-grant-error">{outcome.error}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="review-row">
                <div className="field-label">Duration</div>
                <div className="review-duration">
                  <span className="review-duration-label">{duration.label}</span>
                  <span className="muted">
                    {duration.days === null
                      ? 'No expiry date — access stays until revoked'
                      : `${duration.note} · until ${expiryDate(duration.days)}`}
                  </span>
                </div>
              </div>
            </div>

            <div className="wizard-note-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="#0c6e5f" strokeWidth="2" />
                <path
                  d="M12 11v5"
                  stroke="#0c6e5f"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M12 7.6v.1"
                  stroke="#0c6e5f"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <p>
                {count === 1
                  ? `The role is applied in ${entries[0]?.system.name ?? 'the system'} straight away and the grant is recorded in the audit log under your name.`
                  : `The roles are applied in all ${count} systems straight away, one after another, and every grant is recorded in the audit log under your name.`}
                {replacing.length > 0 &&
                  ` The previous ${replacing.length === 1 ? 'role' : 'roles'} on ${replacing
                    .map((entry) => entry.system.name)
                    .join(', ')} ${replacing.length === 1 ? 'is' : 'are'} revoked in the same step.`}{' '}
                You can revoke access at any time from {user.name}'s profile.
              </p>
            </div>
          </div>
        )}

        {error && <div className="modal-error wizard-error">{error}</div>}

        <div className="wizard-footer">
          <button
            className="btn-secondary"
            disabled={submitting}
            onClick={() => (started || step === 0 ? back() : setStep(step - 1))}
          >
            {started ? 'Done' : step === 0 ? 'Cancel' : 'Back'}
          </button>
          <div className="wizard-footer-end">
            <span className="wizard-hint">{hint}</span>
            {step < STEPS.length - 1 ? (
              <button
                className="btn-primary"
                disabled={!canContinue}
                onClick={() => setStep(step + 1)}
              >
                Continue
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M5 12h13M12 6l6 6-6 6"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            ) : started ? (
              <button
                className="btn-primary"
                disabled={submitting || failed.length === 0}
                onClick={() => void submit(failed)}
              >
                <CheckIcon size={15} stroke={2.6} />
                {submitting ? 'Granting…' : 'Retry failed'}
              </button>
            ) : (
              <button
                className="btn-primary"
                disabled={submitting || !allRolesChosen(selection)}
                onClick={() => void submit(selectedIds(selection))}
              >
                <CheckIcon size={15} stroke={2.6} />
                {submitting
                  ? 'Granting…'
                  : count > 1
                    ? `Grant access to ${count} systems`
                    : 'Grant access'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
